import { Component } from "react";

/**
 * ErrorBoundary — catches render-time errors anywhere in its subtree and
 * shows a recovery UI instead of letting React unmount the whole app
 * (which results in a blank white screen — a particularly bad UX on
 * Android WebView where there's no devtools to investigate).
 *
 * The boundary surfaces the error message itself so the user can
 * screenshot it for support, AND offers a "Reload" button that re-mounts
 * the route by toggling the boundary's own retry-count state.
 *
 * Usage:
 *   <ErrorBoundary scope="profile">
 *     <Profile />
 *   </ErrorBoundary>
 *
 * Props:
 *   children   — the subtree to protect
 *   scope      — short identifier shown in the recovery UI (for support)
 *   fallback   — optional custom render: ({ error, reset }) => ReactNode
 */
export default class ErrorBoundary extends Component {
  state = { error: null, info: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to console so Android Studio Logcat captures it. We do NOT
    // ship to a remote logger here — privacy & cost.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", this.props.scope || "(root)", error, info);

    // 23 Tem 2026: Deploy sonrası "Loading chunk failed". Yeni deploy chunk
    // hash'lerini değiştirir; açık eski sekme artık var olmayan eski chunk'ı
    // isteyince patlar. index.html no-cache olduğu için sayfayı BİR KEZ
    // tazelemek taze main.js + doğru chunk'ları getirir ve kullanıcı bu hata
    // ekranını hiç görmemiş gibi devam eder. Sonsuz döngüyü sessionStorage
    // ile engelliyoruz: en fazla 2 kez, ≥10 sn arayla.
    const msg = String(error?.message || error || "");
    const isChunkError =
      /loading chunk [\w-]+ failed/i.test(msg) ||
      /chunkloaderror/i.test(msg) ||
      /loading css chunk/i.test(msg) ||
      /error loading dynamically imported module/i.test(msg) ||
      /failed to fetch dynamically imported module/i.test(msg);
    if (isChunkError) {
      try {
        const n = Number(sessionStorage.getItem("__cr_n__") || 0);
        const last = Number(sessionStorage.getItem("__cr_t__") || 0);
        if (n < 2 && Date.now() - last > 10000) {
          sessionStorage.setItem("__cr_n__", String(n + 1));
          sessionStorage.setItem("__cr_t__", String(Date.now()));
          window.location.reload();
          return;
        }
      } catch { /* sessionStorage kapalıysa sessizce hata ekranını göster */ }
    }
    this.setState({ info });
  }

  reset = () => {
    this.setState({ error: null, info: null });
  };

  reload = () => {
    try {
      window.location.reload();
    } catch {
      this.reset();
    }
  };

  render() {
    if (!this.state.error) return this.props.children;
    if (typeof this.props.fallback === "function") {
      return this.props.fallback({ error: this.state.error, reset: this.reset });
    }
    const msg = String(this.state.error?.message || this.state.error || "Bilinmeyen hata");
    const stack = String(this.state.error?.stack || "").split("\n").slice(0, 6).join("\n");
    return (
      <div
        data-testid={`error-boundary-${this.props.scope || "root"}`}
        style={{
          maxWidth: 640,
          margin: "10vh auto",
          padding: 24,
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        <div
          style={{
            background: "#fff",
            border: "1px solid #fecaca",
            borderRadius: 16,
            padding: 24,
            boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ fontSize: 28, marginBottom: 8 }}>🐾</div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: "#1c1917",
              margin: 0,
              marginBottom: 6,
            }}
          >
            Bir şeyler ters gitti
          </h1>
          <p style={{ color: "#57534e", fontSize: 14, marginBottom: 16 }}>
            Bu ekran beklenmedik bir hata verdi. Aşağıdaki butonla yeniden yükleyebilir
            veya başka bir sekmeye geçebilirsin.
          </p>
          <details
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
              padding: 10,
              fontSize: 12,
              color: "#991b1b",
              marginBottom: 16,
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Hata detayı ({this.props.scope || "root"})
            </summary>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                marginTop: 8,
                fontFamily: "ui-monospace, monospace",
                fontSize: 11,
              }}
            >
              {msg}
              {stack ? `\n\n${stack}` : ""}
            </pre>
          </details>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={this.reload}
              data-testid="error-boundary-reload-btn"
              style={{
                background: "#1c1917",
                color: "white",
                border: "none",
                borderRadius: 12,
                padding: "10px 16px",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Sayfayı yeniden yükle
            </button>
            <button
              type="button"
              onClick={() => {
                this.reset();
                try { window.history.back(); } catch { /* noop */ }
              }}
              data-testid="error-boundary-back-btn"
              style={{
                background: "white",
                color: "#1c1917",
                border: "1px solid #e7e5e4",
                borderRadius: 12,
                padding: "10px 16px",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Geri
            </button>
          </div>
        </div>
      </div>
    );
  }
}
